import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Skeleton } from '../Skeleton';
import { Button } from '../Button';
import { Badge } from '../Badge';
import {
  NotificationBody,
  type EntityRef,
} from '../notifications';

import { ChevronRightIcon } from './icons';
import {
  fetchLatestNotifications,
  markAllNotificationsRead,
  markNotificationRead,
  type BellNotification,
} from './notifications-api';

export const UNREAD_QUERY_KEY = ['notifications', 'unread-count'] as const;
const LATEST_QUERY_KEY = ['notifications', 'latest'] as const;

interface Props {
  open: boolean;
  notificationsTo: string;
  onClose: () => void;
}

export function NotificationsDrawerContent({
  open,
  notificationsTo,
  onClose,
}: Props): JSX.Element {
  const { t } = useTranslation('notifications');
  const qc = useQueryClient();

  const list = useQuery<BellNotification[]>({
    queryKey: LATEST_QUERY_KEY,
    queryFn: () => fetchLatestNotifications(20),
    enabled: open,
    staleTime: 15_000,
  });

  const markAll = useMutation({
    mutationFn: markAllNotificationsRead,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: UNREAD_QUERY_KEY });
      void qc.invalidateQueries({ queryKey: LATEST_QUERY_KEY });
    },
  });

  const markOne = useMutation({
    mutationFn: markNotificationRead,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: UNREAD_QUERY_KEY });
      void qc.invalidateQueries({ queryKey: LATEST_QUERY_KEY });
    },
  });

  const rows = list.data ?? [];
  const hasUnread = rows.some((r) => r.status === 'sent');

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between gap-2 pb-3">
        <span className="text-xs text-muted-foreground">
          {t('drawer.subtitle')}
        </span>
        <Button
          size="sm"
          variant="secondary"
          disabled={!hasUnread || markAll.isPending}
          onClick={() => markAll.mutate()}
          data-testid="notifications-mark-all-read"
        >
          {markAll.isPending ? t('drawer.marking_all') : t('drawer.mark_all')}
        </Button>
      </div>

      <ul
        className="-mx-2 flex-1 space-y-1 overflow-y-auto"
        data-testid="notifications-drawer-list"
      >
        {list.isLoading ? (
          <DrawerSkeletons />
        ) : rows.length === 0 ? (
          <li className="px-2 py-6 text-center text-sm text-muted-foreground">
            {t('drawer.empty')}
          </li>
        ) : (
          rows.map((row) => (
            <DrawerRow
              key={row.id}
              row={row}
              onMarkRead={() => markOne.mutate(row.id)}
            />
          ))
        )}
      </ul>

      <div className="border-t border-border pt-3">
        <Link
          to={notificationsTo}
          onClick={onClose}
          className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
          data-testid="notifications-open-page"
        >
          {t('drawer.open_page')}
          <ChevronRightIcon size={14} />
        </Link>
      </div>
    </div>
  );
}

function DrawerSkeletons(): JSX.Element {
  return (
    <>
      {[0, 1, 2, 3].map((i) => (
        <li key={i} className="px-2 py-2">
          <Skeleton className="mb-1 h-4 w-3/4" />
          <Skeleton className="h-3 w-1/2" />
        </li>
      ))}
    </>
  );
}

interface RowProps {
  row: BellNotification;
  onMarkRead: () => void;
}

function DrawerRow({ row, onMarkRead }: RowProps): JSX.Element {
  const { t } = useTranslation('notifications');
  const unread = row.status === 'sent';
  const payload = (row.payload ?? {}) as {
    template_vars?: Record<string, string>;
    entity_refs?: EntityRef[];
  };
  return (
    <li
      data-testid="notifications-drawer-row"
      data-unread={unread ? 'true' : 'false'}
      className="rounded-md px-2 py-2 hover:bg-muted"
    >
      <div className="flex items-start gap-2">
        <span
          aria-hidden
          className={`mt-1 inline-block h-2 w-2 shrink-0 rounded-full ${
            unread ? 'bg-primary' : 'bg-transparent'
          }`}
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate text-sm font-medium text-foreground">
              <NotificationBody
                kind={row.kind}
                template_vars={payload.template_vars ?? {}}
                entity_refs={payload.entity_refs ?? []}
                field="subject"
                fallback={row.title || row.kind}
              />
            </span>
            {row.channel === 'email' ? (
              <Badge variant="secondary">{t('channels.email')}</Badge>
            ) : null}
          </div>
          <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">
            <NotificationBody
              kind={row.kind}
              template_vars={payload.template_vars ?? {}}
              entity_refs={payload.entity_refs ?? []}
              field="body"
              fallback={row.body}
            />
          </p>
          <div className="mt-1 flex items-center justify-between text-[11px] text-muted-foreground">
            <time dateTime={row.created_at}>
              {new Date(row.created_at).toLocaleString()}
            </time>
            {unread ? (
              <button
                type="button"
                onClick={onMarkRead}
                className="text-primary hover:underline"
              >
                {t('drawer.mark_read')}
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </li>
  );
}
